<template>
    <div class="view-container api-usage">
        <div class="api-usage-filters">
            <div class="api-usage-filter">
                <span class="api-usage-label">{{ $t('Date') }}</span>
                <datepicker :showTime="true" :dateFrom="dateFrom" :dateTo="dateTo" :min="new Date(-8640000000000000)" :max="new Date()" @dateChanged="dateChanged"></datepicker>
            </div>
            <div class="api-usage-filter">
                <span class="api-usage-label">{{ $t('ApiUsageLayout') }}</span>
                <b-form-radio-group v-model="groupBy" :options="groupByOptions" buttons button-variant="outline-secondary" size="sm" @change="getData"></b-form-radio-group>
            </div>
            <div class="api-usage-filter" style="min-width: 260px;">
                <span class="api-usage-label">{{ $t('Users') }}</span>
                <multiselect v-model="selectedUsers" :options="users" :multiple="true" :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                             label="name" track-by="id" :placeholder="$t('All')" select-label="" deselect-label="" selected-label="" @input="getData">
                    <template slot="selection" slot-scope="{ values, isOpen }">
                        <span class="multiselect__single" v-if="values.length && !isOpen">{{ values.length }} {{ $t('OptionsSelected') }}</span>
                    </template>
                    <template slot="tag"><span></span></template>
                </multiselect>
            </div>
            <div class="api-usage-filter" style="min-width: 320px;">
                <span class="api-usage-label">{{ $t('ApiUsageEndpoint') }}</span>
                <multiselect v-model="selectedEndpoint" :options="endpoints" label="label" track-by="label" :placeholder="$t('All')"
                             select-label="" deselect-label="" selected-label="" :allow-empty="true" @input="getData"></multiselect>
            </div>
            <div class="api-usage-filter">
                <span class="api-usage-label">{{ $t('Roles') }}</span>
                <b-form-checkbox-group v-model="selectedRoles" :options="roleOptions" @change="getData"></b-form-checkbox-group>
            </div>
        </div>

        <div class="api-usage-chart">
            <div class="api-usage-chart-title">
                <span>{{ chartTitle }}</span>
                <a v-if="selectedRow" href="#" @click.prevent="clearSelection">{{ $t('ApiUsageShowAll') }}</a>
            </div>
            <sci-chart-api-usage :points="timeline" :hourly="isHourly"></sci-chart-api-usage>
        </div>

        <div class="api-usage-grid">
            <ejs-grid ref="grid" :key="groupBy" height="100%" width="100%" :toolbar="toolbar" :toolbarClick="toolbarClick"
                      :dataSource="rows" :allowPaging="true" :pageSettings="pageSettings"
                      :allowFiltering="true" :filterSettings="filterOptions" :allowSorting="true" :sortSettings="sortSettings"
                      :allowExcelExport="true" :showBusyIndicator="true"
                      :childGrid="childGrid" :detailDataBound="onDetailDataBound" :rowSelected="onRowSelected">
                <e-columns>
                    <e-column v-if="groupBy === 'user'" field="name" :headerText="$t('User')" textAlign="Left" width="220px" clipMode="EllipsisWithTooltip"></e-column>
                    <e-column v-if="groupBy === 'user'" field="email" :headerText="$t('Email')" textAlign="Left" width="220px" clipMode="EllipsisWithTooltip"></e-column>
                    <e-column v-if="groupBy === 'user'" field="role" :headerText="$t('Role')" textAlign="Left" width="110px" :filter="filterCheckBox"></e-column>
                    <e-column v-if="groupBy === 'endpoint'" field="method" :headerText="$t('ApiUsageMethod')" textAlign="Left" width="100px" :filter="filterCheckBox"></e-column>
                    <e-column v-if="groupBy === 'endpoint'" field="endpoint" :headerText="$t('ApiUsageEndpoint')" textAlign="Left" width="360px" clipMode="EllipsisWithTooltip"></e-column>
                    <e-column field="count" :headerText="$t('ApiUsageCalls')" textAlign="Right" width="110px" type="number" format="N0"></e-column>
                    <e-column field="errorCount" :headerText="$t('ApiUsageErrors')" textAlign="Right" width="100px" type="number" format="N0"></e-column>
                    <e-column field="avgMs" :headerText="$t('ApiUsageAvgMs')" textAlign="Right" width="110px" type="number" format="N0"></e-column>
                    <e-column field="maxMs" :headerText="$t('ApiUsageMaxMs')" textAlign="Right" width="110px" type="number" format="N0"></e-column>
                    <e-column field="distinctCount" :headerText="groupBy === 'user' ? $t('ApiUsageEndpointsCount') : $t('ApiUsageUsersCount')" textAlign="Right" width="110px" type="number"></e-column>
                    <e-column field="lastSeen" :headerText="$t('ApiUsageLastSeen')" textAlign="Left" type="date" :format="dateTimeFormatOptions" width="150px"></e-column>
                </e-columns>
            </ejs-grid>
        </div>
    </div>
</template>

<script>
import { getApiUsageReport, getApiUsageTimeline, getApiUsageEndpoints, getAllUsersNames } from '@api'
import { Sort, Page, Filter, Toolbar, Search, ExcelExport, DetailRow } from '@syncfusion/ej2-vue-grids';
import Multiselect from 'vue-multiselect'
import moment from 'moment'
import Datepicker from '@/components/Datepicker.vue'
import SciChartApiUsage from '@/components/Charts/SciChartApiUsage.vue'

// Do dwoch dni wykres pokazuje godziny, powyzej - dni.
const HOURLY_RANGE_MS = 2 * 24 * 60 * 60 * 1000;

// Diagnostyka uzycia API (tylko Admin): kto i jak czesto korzysta z ktorych endpointow.
// Dane to godzinowe agregaty zbierane od wdrozenia - wstecz ich nie ma.
export default {
    provide: {
        grid: [ Sort, Page, Filter, Toolbar, Search, ExcelExport, DetailRow ]
    },
    components: {
        Datepicker,
        Multiselect,
        SciChartApiUsage
    },
    data() {
        const now = new Date();
        const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);

        return {
            dateFrom: weekAgo,
            dateTo: now,
            groupBy: 'user',
            groupByOptions: [
                { text: this.$t('ApiUsagePerUser'), value: 'user' },
                { text: this.$t('ApiUsagePerEndpoint'), value: 'endpoint' }
            ],
            users: [],
            selectedUsers: [],
            endpoints: [],
            selectedEndpoint: null,
            roleOptions: ['Admin', 'Service', 'User', 'Server', 'PC', 'Anonymous'],
            selectedRoles: ['Admin', 'Service', 'User'],

            rows: [],
            timeline: [],
            selectedRow: null,
            requestId: 0,

            pageSettings: { pageSize: 50, pageSizes: [25, 50, 100, 500] },
            sortSettings: { columns: [{ field: 'count', direction: 'Descending' }] },
            toolbar: [
                { text: this.$t('ApiUsage'), align: 'Left', id: 'table-title' },
                { text: this.$t('ClearFilter'), id: 'clear-filters', prefixIcon: 'e-cancel', align: 'Right' },
                { text: 'Excel', id: 'excel-export', prefixIcon: 'e-excelexport', align: 'Right' },
                'Search'
            ],
            filterOptions: { type: 'Menu' },
            filterCheckBox: { type: 'CheckBox' },
            dateTimeFormatOptions: { format: 'yyyy-MM-dd HH:mm', type: 'dateTime' }
        }
    },
    computed: {
        isHourly() {
            return this.dateTo - this.dateFrom <= HOURLY_RANGE_MS;
        },
        chartTitle() {
            if (!this.selectedRow) {
                return this.$t('ApiUsageCallsOverTime');
            }
            return `${this.$t('ApiUsageCallsOverTime')}: ${this.selectedRow.name}`;
        },
        // Detal wiersza to drugi wymiar: endpointy uzytkownika albo uzytkownicy endpointu.
        childGrid() {
            const columns = this.groupBy === 'user'
                ? [
                    { field: 'method', headerText: this.$t('ApiUsageMethod'), width: 90 },
                    { field: 'endpoint', headerText: this.$t('ApiUsageEndpoint'), width: 360 }
                ]
                : [
                    { field: 'name', headerText: this.$t('User'), width: 220 },
                    { field: 'role', headerText: this.$t('Role'), width: 110 }
                ];

            return {
                queryString: 'key',
                allowSorting: true,
                sortSettings: { columns: [{ field: 'count', direction: 'Descending' }] },
                columns: columns.concat([
                    { field: 'count', headerText: this.$t('ApiUsageCalls'), textAlign: 'Right', width: 110, format: 'N0' },
                    { field: 'errorCount', headerText: this.$t('ApiUsageErrors'), textAlign: 'Right', width: 100, format: 'N0' },
                    { field: 'avgMs', headerText: this.$t('ApiUsageAvgMs'), textAlign: 'Right', width: 110, format: 'N0' },
                    { field: 'maxMs', headerText: this.$t('ApiUsageMaxMs'), textAlign: 'Right', width: 110, format: 'N0' },
                    { field: 'lastSeen', headerText: this.$t('ApiUsageLastSeen'), width: 150, type: 'date', format: this.dateTimeFormatOptions }
                ])
            };
        }
    },
    methods: {
        // Daty bez strefy - backend przechowuje czas lokalny serwera, jak reszta bazy.
        formatDate(date) {
            return moment(date).format('YYYY-MM-DDTHH:mm:ss');
        },
        baseParams() {
            const params = {
                from: this.formatDate(this.dateFrom),
                to: this.formatDate(this.dateTo),
                roles: this.selectedRoles.join(',')
            };

            if (this.selectedUsers.length > 0) {
                params.userIds = this.selectedUsers.map(x => x.id).join(',');
            }

            if (this.selectedEndpoint) {
                params.method = this.selectedEndpoint.method;
                params.endpoint = this.selectedEndpoint.endpoint;
            }

            return params;
        },
        // Zawezenie parametrow do jednego wiersza glownej tabeli (detal i wykres).
        rowParams(row) {
            const params = this.baseParams();

            if (this.groupBy === 'user') {
                // Pusty userId (zadania bez tokenu) oznaczalby "bez filtra" - zawezamy wtedy po roli.
                if (row.userId) {
                    params.userIds = row.userId;
                } else {
                    params.roles = 'Anonymous';
                }
            } else {
                params.method = row.method;
                params.endpoint = row.endpoint;
            }

            return params;
        },
        mapRow(row) {
            const name = this.groupBy === 'endpoint'
                ? `${row.method} ${row.endpoint}`
                : (row.userName || row.email || row.userId || this.$t('Anonymous'));

            return Object.assign({}, row, {
                key: this.groupBy === 'user' ? row.userId : `${row.method} ${row.endpoint}`,
                name: name,
                avgMs: Math.round(row.avgMs),
                lastSeen: row.lastSeen ? new Date(row.lastSeen) : null
            });
        },
        mapChildRow(row) {
            return Object.assign({}, row, {
                name: row.userName || row.email || row.userId || this.$t('Anonymous'),
                avgMs: Math.round(row.avgMs),
                lastSeen: row.lastSeen ? new Date(row.lastSeen) : null
            });
        },
        async getData() {
            // Filtry zmieniaja sie szybko - odpowiedz starszego zapytania nie moze nadpisac nowszej.
            const requestId = ++this.requestId;
            this.selectedRow = null;

            this.$store.dispatch('set_loading', true);
            try {
                const params = this.baseParams();
                const [rows, timeline] = await Promise.all([
                    getApiUsageReport(Object.assign({ groupBy: this.groupBy }, params)),
                    getApiUsageTimeline(Object.assign({ bucket: this.isHourly ? 'hour' : 'day' }, params))
                ]);

                if (requestId !== this.requestId) {
                    return;
                }

                this.rows = rows.map(this.mapRow);
                this.timeline = timeline;
            } finally {
                this.$store.dispatch('set_loading', false);
            }
        },
        async getTimelineFor(row) {
            const params = row ? this.rowParams(row) : this.baseParams();
            params.bucket = this.isHourly ? 'hour' : 'day';

            this.timeline = await getApiUsageTimeline(params);
        },
        dateChanged(from, to) {
            this.dateFrom = from;
            this.dateTo = to;
            this.getData();
        },
        onRowSelected(args) {
            if (!args.data || args.data.key === undefined) {
                return;
            }
            this.selectedRow = args.data;
            this.getTimelineFor(args.data);
        },
        clearSelection() {
            this.selectedRow = null;
            if (this.$refs.grid) {
                this.$refs.grid.clearSelection();
            }
            this.getTimelineFor(null);
        },
        async onDetailDataBound(args) {
            const params = this.rowParams(args.data);
            params.groupBy = this.groupBy === 'user' ? 'endpoint' : 'user';

            const result = await getApiUsageReport(params);
            const childGrid = args.detailElement.querySelector('.e-grid').ej2_instances[0];
            // Syncfusion filtruje detal warunkiem child[queryString] == parent[queryString],
            // wiec kazdy wiersz detalu musi niesc klucz rodzica - inaczej detal jest pusty.
            childGrid.dataSource = result.map(row => Object.assign(this.mapChildRow(row), { key: args.data.key }));
        },
        toolbarClick(args) {
            switch (args.item.id) {
                case 'clear-filters':
                    this.$refs.grid.clearFiltering();
                    break;
                case 'excel-export':
                    this.$refs.grid.excelExport({ fileName: `api-usage-${this.groupBy}-${moment().format('YYYY-MM-DD')}.xlsx` });
                    break;
            }
        }
    },
    async mounted() {
        const [users, endpoints] = await Promise.all([getAllUsersNames(), getApiUsageEndpoints()]);

        this.users = users;
        this.endpoints = endpoints.map(x => Object.assign({ label: `${x.method} ${x.endpoint}` }, x));

        // Pierwsze pobranie danych robi dateChanged - Datepicker emituje zakres zaraz po zamontowaniu.
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.api-usage {
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: auto;
}

.api-usage-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
}

.api-usage-filter {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.api-usage-label {
    font-size: 12px;
    color: var(--text-muted);
}

.api-usage-chart {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 10px;
}

.api-usage-chart-title {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
    color: var(--text-strong);
}

.api-usage-grid {
    flex: 1;
    min-height: 400px;
}
</style>



// WEBPACK FOOTER //
// src/views/ApiUsage.vue